import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { PDFTemplateVisibility } from '../types';

export interface PDFExportOptions {
  filename?: string;
  title?: string;
  orientation?: 'portrait' | 'landscape';
  templateVisibility?: PDFTemplateVisibility;
}

export class PDFExporter {
  static async exportElementToPDF(
    elementId: string,
    options: PDFExportOptions = {}
  ): Promise<void> {
    const {
      filename = 'document.pdf',
      title,
      orientation = 'portrait',
      templateVisibility,
    } = options;

    try {
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error(`Element with id "${elementId}" not found`);
      }

      // Clone the element to work on a copy instead of the original
      const clonedElement = element.cloneNode(true) as HTMLElement;

      // Position the clone off-screen but still rendered
      // Set width to 8.5 inches (816px at 96 DPI) for US Letter paper
      // With 0.3in margins on each side, content is ~7.9in = 758px
      clonedElement.style.position = 'absolute';
      clonedElement.style.left = '-9999px';
      clonedElement.style.top = '0';
      clonedElement.style.width = '816px';
      clonedElement.style.maxWidth = '816px';
      clonedElement.style.boxSizing = 'border-box';

      document.body.appendChild(clonedElement);

      // Hide print buttons in the clone
      const printButtons = clonedElement.querySelectorAll('[data-no-print]');
      printButtons.forEach((btn) => {
        (btn as HTMLElement).style.display = 'none';
      });

      // Apply template visibility filtering to the clone if provided
      if (templateVisibility) {
        // Hide materials entire section if showMaterials is false
        if (!templateVisibility.showMaterials) {
          const materialTables = clonedElement.querySelectorAll('[data-pdf-materials="true"]');
          materialTables.forEach((table) => {
            const parentDiv = (table as HTMLElement).closest('.asset-detail');
            if (parentDiv) {
              (parentDiv as HTMLElement).style.display = 'none';
            }
          });
        }

        const visibilityRules = [
          { selector: '[data-pdf-material-breakdown]', key: 'showMaterialBreakdown' },
          { selector: '[data-pdf-markup]', key: 'showMarkupDetails' },
          { selector: '[data-pdf-process-costs]', key: 'showProcessCosts' },
          { selector: '[data-pdf-item-totals]', key: 'showItemTotals' },
          { selector: '[data-pdf-section-totals]', key: 'showSectionTotals' },
          { selector: '[data-pdf-unit-costs]', key: 'showUnitCosts' },
          { selector: '[data-pdf-processes]', key: 'showProcesses' },
        ] as const;

        visibilityRules.forEach(({ selector, key }) => {
          const elements = clonedElement.querySelectorAll(selector);
          const isVisible = templateVisibility[key as keyof PDFTemplateVisibility];

          elements.forEach((el) => {
            if (!isVisible) {
              (el as HTMLElement).style.display = 'none';
            }
          });
        });
      }

      // Create canvas from the cloned element
      const canvas = await html2canvas(clonedElement, {
        allowTaint: true,
        useCORS: true,
        scale: 2,
        backgroundColor: '#ffffff',
      });

      // Remove the cloned element from the DOM
      document.body.removeChild(clonedElement);

      // Calculate dimensions
      const imgWidth = orientation === 'portrait' ? 210 : 297; // A4 dimensions in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pageHeight = orientation === 'portrait' ? 297 : 210;
      let heightLeft = imgHeight;

      // Create PDF
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: 'a4',
      });

      let position = 0;

      // Add first page
      let imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Add title if provided
      if (title) {
        pdf.setProperties({
          title: title,
        });
      }

      // Save PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      throw error;
    }
  }

  static async exportHTMLToPDF(
    html: string,
    options: PDFExportOptions = {}
  ): Promise<void> {
    const {
      filename = 'document.pdf',
      title,
      orientation = 'portrait',
    } = options;

    try {
      // Create a temporary container
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = html;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      document.body.appendChild(tempContainer);

      // Create canvas
      const canvas = await html2canvas(tempContainer, {
        allowTaint: true,
        useCORS: true,
        scale: 2,
        backgroundColor: '#ffffff',
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      // Calculate dimensions
      const imgWidth = orientation === 'portrait' ? 210 : 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pageHeight = orientation === 'portrait' ? 297 : 210;
      let heightLeft = imgHeight;

      // Create PDF
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: 'a4',
      });

      let position = 0;
      let imgData = canvas.toDataURL('image/png');

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Add title if provided
      if (title) {
        pdf.setProperties({
          title: title,
        });
      }

      // Save PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      throw error;
    }
  }
}
